import { useState } from 'react';
import { useDelete } from 'react-admin';

import { GenericObject } from '@app/components/common/Table/DefaultTable/types';

import { useErrorHandling } from './useErrorHandling';

const useDeleteResource = (
  resource: string,
  resourceSingular: string,
  deleteCallback?: Function,
  resolveDeleteConfirmationContent?: Function
) => {
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [deleteContent, setDeleteContent] = useState<unknown>('');
  const [deletingId, setDeletingId] = useState<unknown>(null);

  const [handleError] = useErrorHandling();

  const [deleteOne, { isLoading: isDeleteLoading }] = useDelete(undefined, undefined, {
    onError: (error: any) => {
      handleError(error);
    },
    onSuccess: () => {
      deleteCallback && deleteCallback(deletingId as number);
    },
  });

  const getDeleteConfirmationContent = async (deletingId: number) => {
    if (resolveDeleteConfirmationContent) {
      return await resolveDeleteConfirmationContent(deletingId);
    }
    return {
      deletable: true,
    };
  };

  const onDelete = async (row: GenericObject) => {
    const deleteConfirmation = await getDeleteConfirmationContent(row?.id as number);
    if (deleteConfirmation.deletable) {
      setDeleteContent(deleteConfirmation.message || `Are you sure you want to delete this ${resourceSingular}?`);
      setDeletingId(row?.id);
      setIsDeleteConfirmationOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteOne(resource, { id: deletingId as number });
    } catch (error: any) {
      handleError(error);
    } finally {
      handleDeleteDialogClose();
    }
  };

  const handleDeleteDialogClose = () => {
    setDeletingId(null);
    setIsDeleteConfirmationOpen(false);
  };

  return {
    isDeleteLoading,
    onDelete,
    handleConfirmDelete,
    handleDeleteDialogClose,
    isDeleteConfirmationOpen,
    deleteContent,
  };
};

export default useDeleteResource;
